import { CHAT_EVENTS } from '@conversed/shared/src/central/constants/socket';
import type {
  TAssignee,
  TConversationUser,
} from '@conversed/shared/src/central/types/chat.types';
import {
  addValueToList,
  removeValueFromList,
  replaceValueInList,
} from '@conversed/shared/src/helpers/data.helpers';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { io, type Socket } from 'socket.io-client';

import { client } from '../api/rest/client';
import { socketIOSettings } from '../config/socket.io';
import { Actions } from '../constants/actionTypes';
import { WS_ENDPOINT } from '../constants/env';
import { CHAT_PAGE_ROUTE } from '../constants/routes';
import type { TCurrentUser } from '../interfaces/IAuth';
import type {
  TNewCustomerMessagePayload,
  TNewCustomerNotificationPayload,
} from '../interfaces/IMessage';
import type { TChatInbox, TConversation } from '../interfaces/TChat';
import { mainActions } from '../redux/reducers/mainHandler';
import { useChatNotificationsActions } from './chat/notifications/useChatNotificationsActions';
import { useMainDispatch } from './useMainDispatch';
import { useMainSelector } from './useMainSelector';

const { CORE_TO_PLATFORM_EVENTS: RECEIVE_EVENTS } = CHAT_EVENTS;

export const useSocketEvents = () => {
  const {
    chat: { selectedConversation, chatList, inboxes },
    user: { currentUser: currentlyLoggedInUser },
    login: { accessToken },
  } = useMainSelector();

  const dispatch = useMainDispatch();
  const newDispatch = useDispatch();
  const navigate = useNavigate();

  const socketRef = useRef<Socket | null>(null);
  const userRef = useRef<TCurrentUser | undefined>(currentlyLoggedInUser);
  const inboxesRef = useRef<TChatInbox[] | undefined>(inboxes);
  const selectedConversationRef = useRef<TConversation | undefined>(
    selectedConversation,
  );
  const chatListRef = useRef<TConversation[]>(chatList);

  const { addNotification: storeNotification } = useChatNotificationsActions();

  // biome-ignore lint/correctness/useExhaustiveDependencies: uses no actual dependencies
  const newUser = useCallback((messagePayload: { user: TConversation }) => {
    console.debug(
      `Event received: ${RECEIVE_EVENTS.NEW_USER} - calling newUser`,
    );
    const { user } = messagePayload;

    if (user?.id) {
      dispatch({
        type: Actions.SET_CHAT,
        payload: {
          chatList: addValueToList(user, chatListRef.current),
        },
      });
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: uses no actual dependencies
  const addMessage = useCallback(
    (messagePayload: TNewCustomerMessagePayload) => {
      console.debug(
        `Event received: ${RECEIVE_EVENTS.NEW_USER_MESSAGE} - calling addMessage with payload:`,
        messagePayload,
      );

      const { message, fromUser } = messagePayload;
      const { direction, time } = message;

      const currentUserId = selectedConversationRef.current?.id;

      if (direction === 'in') {
        if (fromUser === currentUserId) {
          console.debug('Updating message list');
          dispatch({
            type: Actions.SET_CHAT_MESSAGES,
            payload: addValueToList(
              message,
              selectedConversationRef?.current?.messages,
            ),
          });
          dispatch({
            type: Actions.SET_CONVERSATION_DATE,
            payload: { date: time, chatId: currentUserId },
          });
        } else {
          console.debug(
            'Received message for another user. Not updating message list',
            {
              fromUser,
              currentUser: currentUserId,
            },
          );
        }
      }
    },
    [],
  );

  const addNotification = useCallback(
    (messagePayload: TNewCustomerNotificationPayload) => {
      console.debug(
        `Event received: ${RECEIVE_EVENTS.NEW_USER_MESSAGE} - calling addNotification with payload:`,
        messagePayload,
      );

      const { message, messageType, fromUser } = messagePayload;
      const { timestamp } = message;
      const currentUserId = selectedConversationRef.current?.id;

      // TODO: Add exception to notifications for users no in inbox of agent

      console.debug(
        'Received message for user in inbox. Updating notification for user',
        {
          fromUser,
          currentUser: currentUserId,
        },
      );
      storeNotification(fromUser, messageType, timestamp);
    },
    [storeNotification],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: uses no actual dependencies
  const closeChat = useCallback(
    (messagePayload: { user: TConversationUser }) => {
      console.debug(
        `Event received: ${RECEIVE_EVENTS.CLOSE_CONVERSATION} - calling closeChat`,
      );
      const { id: chatBotUserId } = messagePayload.user;

      if (!chatBotUserId) {
        return;
      }

      const currentUserId = selectedConversationRef.current?.id;

      const dispatchPalyoad = {
        chatList: removeValueFromList<TConversation>(
          (chatUser) => chatUser.id === chatBotUserId,
          chatListRef.current,
        ),
        currentUser: undefined,
        currentUserMessages: [],
      };

      // Reset the selectedConversation state if the agent finished/resolved a conversation.
      // Therefore, the resolved conversation will be closed on other browser tabs as well.
      if (currentUserId === chatBotUserId) {
        Object.assign(dispatchPalyoad, {
          selectedConversation: undefined,
          isCannedResponsesMenuOpen: false,
        });
        navigate({ pathname: CHAT_PAGE_ROUTE });
      }

      dispatch({
        type: Actions.SET_CHAT,
        payload: dispatchPalyoad,
      });
    },
    [],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: uses no actual dependencies
  const removeUserFromWaitingList = useCallback(async (messagePayload: any) => {
    const {
      user: { id: chatBotUserId, assignedToAgent: agentId },
    } = messagePayload;

    if (
      chatBotUserId &&
      (agentId === null || agentId === userRef.current?.id)
    ) {
      const { data: labels } = await client.chat.getConversationLabels({
        params: {
          conversationId: chatBotUserId,
        },
      });

      let assignedAgent: TAssignee | undefined;
      if (agentId && agentId === userRef.current?.id) {
        assignedAgent = {
          id: agentId,
          email: userRef.current?.email,
          firstName: userRef.current?.firstName,
          lastName: userRef.current?.lastName,
        };
      }

      const payload = {
        chatList: replaceValueInList<TConversation>(
          {
            ...messagePayload.user,
            labels,
            assignedAgent: assignedAgent,
          },
          chatListRef.current,
          (user) => user.id === chatBotUserId,
        ),
      };

      if (
        // assigned user
        selectedConversationRef.current?.id &&
        agentId === userRef.current?.id
      ) {
        Object.assign(payload, {
          selectedConversation: {
            ...selectedConversationRef.current,
            assignedAgent: assignedAgent,
          },
        });
      } else if (
        // unassigned user
        selectedConversationRef.current?.id === chatBotUserId &&
        !agentId
      ) {
        Object.assign(payload, {
          selectedConversation: undefined,
          isCannedResponsesMenuOpen: false,
        });
        navigate({ pathname: CHAT_PAGE_ROUTE });
      }

      dispatch({
        type: Actions.SET_CHAT,
        payload: payload,
      });
    } else {
      const payload = {
        chatList: removeValueFromList<TConversation>(
          (chatUser) => chatUser.id === chatBotUserId,
          chatListRef.current,
        ),
      };

      if (
        // assinged conversation to another agent than 'me'
        selectedConversationRef.current?.id === chatBotUserId &&
        agentId !== userRef.current?.id
      ) {
        Object.assign(payload, {
          selectedConversation: undefined,
          isCannedResponsesMenuOpen: false,
        });
        navigate({ pathname: CHAT_PAGE_ROUTE });
      }

      dispatch({
        type: Actions.SET_CHAT,
        payload: {
          chatList: removeValueFromList<TConversation>(
            (chatUser) => chatUser.id === chatBotUserId,
            chatListRef.current,
          ),
        },
      });
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: uses no actual dependencies
  const assignUserToInbox = useCallback(async (messagePayload: any) => {
    const { user } = messagePayload;
    const { id: chatBotUserId } = user;

    // * If agent is conversing with the user remove the user
    if (chatBotUserId === selectedConversationRef?.current?.id) {
      navigate(CHAT_PAGE_ROUTE);

      dispatch({
        type: Actions.SET_CHAT,
        payload: {
          selectedConversation: undefined,
          isCannedResponsesMenuOpen: false,
        },
      });
    }

    try {
      const { data: labels } = await client.chat.getConversationLabels({
        params: {
          conversationId: chatBotUserId,
        },
      });

      dispatch({
        type: Actions.SET_CHAT,
        payload: {
          chatList: replaceValueInList<TConversation>(
            { ...user, labels },
            chatListRef.current,
            (user) => user.id === chatBotUserId,
          ),
        },
      });
    } catch (_error) {
      dispatch({
        type: Actions.SET_CHAT,
        payload: {
          chatList: replaceValueInList<TConversation>(
            user,
            chatListRef.current,
            (user) => user.id === chatBotUserId,
          ),
        },
      });
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: uses no actual dependencies
  const changeUserStatus = useCallback((messagePayload: any) => {
    const { user } = messagePayload;
    const { id: chatBotUserId, activityState } = user;

    return newDispatch(
      mainActions.setChatStatusForUser({
        chatId: chatBotUserId,
        activityState,
      }),
    );
  }, []);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  useEffect(() => {
    chatListRef.current = chatList;
  }, [chatList]);

  useEffect(() => {
    userRef.current = currentlyLoggedInUser;
  }, [currentlyLoggedInUser]);

  useEffect(() => {
    inboxesRef.current = inboxes;
  }, [inboxes]);

  // ! I am unsure why this is re-rendering despite the callbacks not having any real dependencies (dispatch, navigate)
  useEffect(() => {
    if (currentlyLoggedInUser?.selectedCompanyId) {
      console.debug('Initializing websocket connection');
      socketRef.current = io(`${WS_ENDPOINT}/portal`, {
        ...socketIOSettings,
        query: {
          'conversed-bot': currentlyLoggedInUser.selectedCompanyId,
        },
        auth: {
          token: accessToken,
        },
      });

      // TODO: Retry if reason is Unauthorized by refreshing token and reconnecting
      socketRef.current.on(
        'connect_error',
        // * Error type has no overlap so we need to any/unknown it
        (err: Error & { data?: { 'retry-ms'?: number; reason?: string } }) => {
          const { data } = err;
          const { 'retry-ms': retryMs } = data || {};

          // * If retryMs then we are rate limited
          if (typeof retryMs === 'number') {
            return console.error(
              `Websocket connection blocked, rate limited, try again in ${
                retryMs * 1000
              } seconds`,
            );
          }

          if (data?.reason) {
            return console.error(
              `Websocket connection blocked, reason ${data?.reason}`,
            );
          }

          return console.error('Unknown websocket error, connection blocked');
        },
      );

      console.debug(
        `Setting up listener for new customer message event - ${RECEIVE_EVENTS.NEW_USER_MESSAGE}`,
      );
      socketRef.current.on(RECEIVE_EVENTS.NEW_USER_MESSAGE, addMessage);
      console.debug(
        `Setting up listener for customer closed event - ${RECEIVE_EVENTS.CLOSE_CONVERSATION}`,
      );
      socketRef.current.on(RECEIVE_EVENTS.CLOSE_CONVERSATION, closeChat);
      console.debug(
        `Setting up listener for remove user from waiting list event - ${RECEIVE_EVENTS.HANDOVER_TO_AGENT_OR_INBOX}`,
      );
      socketRef.current.on(
        RECEIVE_EVENTS.HANDOVER_TO_AGENT_OR_INBOX,
        removeUserFromWaitingList,
      );
      console.debug(
        `Setting up listener for new user to waiting list event - ${RECEIVE_EVENTS.NEW_USER}`,
      );
      socketRef.current.on(RECEIVE_EVENTS.NEW_USER, newUser);

      console.debug(
        `Setting up listener for addition of notification - ${RECEIVE_EVENTS.NEW_USER_NOTIFICATION}`,
      );
      socketRef.current.on(
        RECEIVE_EVENTS.NEW_USER_NOTIFICATION,
        addNotification,
      );

      console.debug(
        `Setting up listener for user assignment to queue - ${RECEIVE_EVENTS.HANDOVER_TO_QUEUE}`,
      );
      socketRef.current.on(RECEIVE_EVENTS.HANDOVER_TO_QUEUE, assignUserToInbox);

      console.debug(
        `Setting up listener for user change status activity - ${RECEIVE_EVENTS.NEW_CHAT_ACTIVITY_STATUS}`,
      );
      socketRef.current.on(
        RECEIVE_EVENTS.NEW_CHAT_ACTIVITY_STATUS,
        changeUserStatus,
      );

      return () => {
        console.debug(
          `Removing listener for new customer message event - ${RECEIVE_EVENTS.NEW_USER_MESSAGE}`,
        );
        socketRef?.current?.off(RECEIVE_EVENTS.NEW_USER_MESSAGE, addMessage);
        console.debug(
          `Removing listener for customer closed event - ${RECEIVE_EVENTS.CLOSE_CONVERSATION}`,
        );
        socketRef?.current?.off(RECEIVE_EVENTS.CLOSE_CONVERSATION, closeChat);
        console.debug(
          `Removing listener for remove user from waiting list event - ${RECEIVE_EVENTS.HANDOVER_TO_AGENT_OR_INBOX}`,
        );
        socketRef?.current?.off(
          RECEIVE_EVENTS.HANDOVER_TO_AGENT_OR_INBOX,
          removeUserFromWaitingList,
        );
        console.debug(
          `Removing listener for new user to waiting list event - ${RECEIVE_EVENTS.NEW_USER}`,
        );
        socketRef?.current?.off(RECEIVE_EVENTS.NEW_USER, newUser);

        console.debug(
          `Removing listener for user assignment to queue - ${RECEIVE_EVENTS.HANDOVER_TO_QUEUE}`,
        );
        socketRef?.current?.off(
          RECEIVE_EVENTS.HANDOVER_TO_QUEUE,
          assignUserToInbox,
        );

        console.debug(
          `Removing listener for addition of notification - ${RECEIVE_EVENTS.NEW_USER_NOTIFICATION}`,
        );
        socketRef?.current?.off(
          RECEIVE_EVENTS.NEW_USER_NOTIFICATION,
          addNotification,
        );

        console.debug(
          `Removing listener for auser status activity - ${RECEIVE_EVENTS.NEW_CHAT_ACTIVITY_STATUS}`,
        );
        socketRef?.current?.off(
          RECEIVE_EVENTS.NEW_CHAT_ACTIVITY_STATUS,
          changeUserStatus,
        );

        console.debug('Closing websocket connection');
        socketRef?.current?.disconnect();
      };
    }
  }, [
    accessToken,
    currentlyLoggedInUser?.selectedCompanyId,
    addMessage,
    newUser,
    closeChat,
    removeUserFromWaitingList,
    assignUserToInbox,
    addNotification,
    changeUserStatus,
  ]);

  return { socketRef };
};

export default useSocketEvents;
